import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import api from '../services/api';

const DEFAULT_LOVE_CODES = [
  { code: '168001', name: '捐贈碼範例' },
  { code: '8585', name: '公益團體' },
];

const CARRIER_TYPES = [
  { code: 'EJ0113', name: 'Email 載具（免填）' },
  { code: '3J0002', name: '手機條碼' },
  { code: 'CQ0001', name: '自然人憑證' },
];

const EInvoiceSelector = ({ value, onChange }) => {
  const [invoiceType, setInvoiceType] = useState(value?.type || 'carrier');
  const [carrierType, setCarrierType] = useState(value?.carrierType || 'EJ0113');
  const [carrierId, setCarrierId] = useState(value?.carrierId || '');
  const [buyerId, setBuyerId] = useState(value?.buyerId || '');
  const [companyName, setCompanyName] = useState(value?.companyName || '');
  const [loveCode, setLoveCode] = useState(value?.loveCode || DEFAULT_LOVE_CODES[0].code);
  const [customLoveCode, setCustomLoveCode] = useState('');

  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [loveCodes, setLoveCodes] = useState(DEFAULT_LOVE_CODES);

  useEffect(() => {
    const fetchLoveCodes = async () => {
      try {
        const res = await api.get('/einvoice/lovecodes');
        if (res.data?.success && Array.isArray(res.data.loveCodes)) setLoveCodes(res.data.loveCodes);
      } catch (e) {
        // ignore
      }
    };
    fetchLoveCodes();
  }, []);

  useEffect(() => {
    const data = {
      type: invoiceType,
      carrierType: invoiceType === 'carrier' ? carrierType : null,
      carrierId: invoiceType === 'carrier' && carrierType !== 'EJ0113' ? carrierId : null,
      buyerId: invoiceType === 'company' ? buyerId : null,
      companyName: invoiceType === 'company' ? companyName : null,
      loveCode: invoiceType === 'donate' ? (loveCode === 'custom' ? customLoveCode : loveCode) : null,
      donate: invoiceType === 'donate',
    };
    onChange(data);
  }, [invoiceType, carrierType, carrierId, buyerId, companyName, loveCode, customLoveCode, onChange]);

  const verifyCarrier = async () => {
    if (!carrierId) {
      setVerifyResult({ success: false, message: '請輸入載具號碼' });
      return;
    }
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await api.post('/einvoice/verify-carrier', { carrier_type: carrierType, carrier_id: carrierId });
      if (res.data?.success && res.data.valid) {
        setVerifyResult({ success: true, message: '載具驗證成功' });
      } else {
        setVerifyResult({ success: false, message: res.data?.message || res.data?.error || '載具驗證失敗' });
      }
    } catch (e) {
      setVerifyResult({ success: false, message: '驗證失敗，請稍後再試' });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
      <Typography variant="subtitle1" fontWeight={900} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <ReceiptIcon color="primary" />
        電子發票
      </Typography>

      <FormControl fullWidth>
        <RadioGroup value={invoiceType} onChange={(e) => { setInvoiceType(e.target.value); setVerifyResult(null); }}>
          <FormControlLabel
            value="carrier"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIphoneIcon fontSize="small" />
                <span>載具（預設）</span>
              </Box>
            }
          />
          <Collapse in={invoiceType === 'carrier'}>
            <Box sx={{ pl: 4, pr: 1, pb: 2 }}>
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>載具類型</InputLabel>
                <Select value={carrierType} label="載具類型" onChange={(e) => { setCarrierType(e.target.value); setVerifyResult(null); }}>
                  {CARRIER_TYPES.map((t) => (
                    <MenuItem key={t.code} value={t.code}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {carrierType === 'EJ0113' ? (
                <Alert severity="info">Email 載具不需要額外填寫。</Alert>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={carrierType === '3J0002' ? '手機條碼' : '載具號碼'}
                    placeholder={carrierType === '3J0002' ? '/AAAAAAA' : '請輸入載具號碼'}
                    value={carrierId}
                    onChange={(e) => { setCarrierId(e.target.value.toUpperCase()); setVerifyResult(null); }}
                    error={!!verifyResult && !verifyResult.success}
                    helperText={verifyResult?.message || ''}
                  />
                  <Button variant="outlined" onClick={verifyCarrier} disabled={verifying} sx={{ minWidth: 96, height: 40 }}>
                    {verifying ? <CircularProgress size={20} /> : '驗證'}
                  </Button>
                </Box>
              )}

              <Collapse in={!!verifyResult && verifyResult.success}>
                <Alert severity="success" sx={{ mt: 1 }}>
                  {verifyResult?.message}
                </Alert>
              </Collapse>
            </Box>
          </Collapse>

          <FormControlLabel
            value="donate"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VolunteerActivismIcon fontSize="small" color="error" />
                <span>捐贈</span>
              </Box>
            }
          />
          <Collapse in={invoiceType === 'donate'}>
            <Box sx={{ pl: 4, pr: 1, pb: 2 }}>
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>捐贈碼</InputLabel>
                <Select value={loveCode} label="捐贈碼" onChange={(e) => setLoveCode(e.target.value)}>
                  {loveCodes.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.name} ({c.code})
                    </MenuItem>
                  ))}
                  <MenuItem value="custom">自訂捐贈碼</MenuItem>
                </Select>
              </FormControl>
              {loveCode === 'custom' && (
                <TextField
                  fullWidth
                  size="small"
                  label="自訂捐贈碼"
                  value={customLoveCode}
                  onChange={(e) => setCustomLoveCode(e.target.value)}
                />
              )}
            </Box>
          </Collapse>

          <FormControlLabel
            value="company"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon fontSize="small" />
                <span>公司戶（統編）</span>
              </Box>
            }
          />
          <Collapse in={invoiceType === 'company'}>
            <Box sx={{ pl: 4, pr: 1, pb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="統一編號"
                value={buyerId}
                onChange={(e) => setBuyerId(e.target.value.replace(/\D/g, '').slice(0, 8))}
                inputProps={{ maxLength: 8 }}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                size="small"
                label="公司名稱（選填）"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </Box>
          </Collapse>
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

export default EInvoiceSelector;

